import { timeAgo } from "../lib/time.js";

/**
 * Client identity card at the bottom of the sidebar.
 * Shows business name, location, and per-data-source status + last-sync time.
 *
 * Props:
 *   client : {
 *     name: string,
 *     location?: string,
 *     dataSources?: [{ type, status }],   // from auth — fallback only
 *   }
 *   syncStatus : {
 *     sources?: [{ type, status, lastSync }],   // fresh from /api/sync/status
 *   }
 *
 * syncStatus.sources (when available) supersedes client.dataSources because
 * lastSync changes after every sync and the auth payload is frozen at login.
 */
const STATUS_COLORS = {
  connected: "bg-positive",
  error: "bg-negative",
  disconnected: "bg-text-muted",
  pending: "bg-text-muted",
};

const SOURCE_LABELS = {
  ga4: "GA4",
  google_ads: "Google Ads",
  shopify: "Shopify",
  meta_ads: "Meta Ads",
  bing_ads: "Bing Ads",
  linkedin_ads: "LinkedIn Ads",
  hubspot: "HubSpot",
  mailchimp: "Mailchimp",
  klaviyo: "Klaviyo",
  callrail: "CallRail",
};

function prettify(type) {
  return SOURCE_LABELS[type] || type.replace(/_/g, " ");
}

export function BusinessCard({ client, syncStatus }) {
  const initial = (client?.name || "?").charAt(0).toUpperCase();
  const sources = syncStatus?.sources?.length
    ? syncStatus.sources
    : client?.dataSources || [];

  return (
    <div className="border-t border-border-light px-4 py-4">
      <div className="flex items-center gap-2.5 mb-3">
        <div
          className="w-9 h-9 rounded bg-accent-bg border border-accent-border flex items-center justify-center text-accent font-semibold text-[14px] flex-shrink-0"
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-text-primary truncate">
            {client?.name}
          </div>
          {client?.location && (
            <div className="text-[10px] text-text-muted truncate">
              {client.location}
            </div>
          )}
        </div>
      </div>

      {sources.length > 0 && (
        <div className="space-y-1.5">
          {sources.map((ds) => (
            <div key={ds.type} className="flex items-center gap-2 min-w-0">
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  STATUS_COLORS[ds.status] || STATUS_COLORS.disconnected
                }`}
                aria-label={ds.status}
              />
              <span
                className="text-[10px] uppercase text-text-muted font-medium flex-1 truncate"
                style={{ letterSpacing: "0.5px" }}
              >
                {prettify(ds.type)}
              </span>
              {ds.lastSync && (
                <span className="text-[9px] text-text-muted flex-shrink-0 lowercase">
                  {timeAgo(ds.lastSync)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
